/**
 * PURPOSE: Administrar uniformes por participante en una matriz tipo hoja de cálculo.
 * RESPONSIBILITIES: Crear columnas repetidas, editar colores localmente y guardar cambios bulk de forma transaccional.
 *   Con opciones normalizadas activas (FTR-0014) queda en solo lectura: se edita en Administración General → Uniformes.
 * COLLABORATORS: uniformMatrixService.js, UniformMatrixCell.jsx y UniformMatrixColumnPicker.jsx.
 * ROUTE: winscore/src/components/UniformMatrix/UniformMatrix.jsx
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { FaSave, FaSync } from "react-icons/fa";
import { getUniformMatrix, saveUniformMatrix } from "../../services/uniformMatrixService.js";
import { useUniformOptionsStatus } from "../../hooks/useUniformOptionsStatus.js";
import { UniformMatrixCell } from "./UniformMatrixCell.jsx";
import { UniformMatrixColumnPicker } from "./UniformMatrixColumnPicker.jsx";
import {
  BASE_COLUMNS,
  getUniformColumnCatalog,
  uniformCellKey,
  uniformColumnKey,
} from "./uniformMatrix.constants.js";

const normalize = (value) => String(value || "").trim();

export function UniformMatrix({ idevent, idsport }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [columns, setColumns] = useState([]);
  const [cells, setCells] = useState({});
  const [baseline, setBaseline] = useState({});
  const [records, setRecords] = useState({});
  const uniformOptions = useUniformOptionsStatus();
  // Mientras se consulta el flag tampoco se ofrece escritura.
  const readOnly = uniformOptions.loading || uniformOptions.enabled;

  const catalog = useMemo(() => getUniformColumnCatalog(idsport), [idsport]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const payload = await getUniformMatrix({ idevent, idsport });
      const nextCells = {};
      const nextRecords = {};
      const columnMap = new Map();
      BASE_COLUMNS.forEach((column) => columnMap.set(uniformColumnKey({ ...column, slotNo: 1 }), { ...column, slotNo: 1 }));
      (payload.uniforms || []).forEach((uniform) => {
        const key = uniformCellKey({ ...uniform, slotNo: uniform.slotNo });
        nextCells[key] = Number(uniform.state) === 1 ? uniform.color || "" : "";
        nextRecords[key] = uniform;
        const base = catalog.find((item) => Number(item.type) === Number(uniform.type) && Number(item.tone) === Number(uniform.tone));
        if (base) columnMap.set(uniformColumnKey(uniform), { ...base, slotNo: Number(uniform.slotNo) || 1 });
      });
      setParticipants(payload.participants || []);
      setCells(nextCells);
      setBaseline(nextCells);
      setRecords(nextRecords);
      setColumns(Array.from(columnMap.values()).sort((a, b) => a.type - b.type || a.tone - b.tone || a.slotNo - b.slotNo));
    } catch (error) {
      console.error(error);
      toast.error("No se pudo cargar la matriz de uniformes");
    } finally {
      setLoading(false);
    }
  }, [catalog, idevent, idsport]);

  useEffect(() => { load(); }, [load]);

  const addColumn = (base) => {
    const slots = columns.filter((column) => column.type === base.type && column.tone === base.tone).map((column) => column.slotNo);
    const slotNo = Math.max(0, ...slots) + 1;
    setColumns((current) => [...current, { ...base, slotNo }].sort((a, b) => a.type - b.type || a.tone - b.tone || a.slotNo - b.slotNo));
  };

  const dirtyKeys = useMemo(() => {
    const keys = new Set([...Object.keys(cells), ...Object.keys(baseline)]);
    return new Set(Array.from(keys).filter((key) => normalize(cells[key]) !== normalize(baseline[key])));
  }, [baseline, cells]);

  const save = async () => {
    if (readOnly) return undefined;
    if (!dirtyKeys.size) return toast("No hay cambios pendientes.");
    const items = [];
    const removed = [];
    dirtyKeys.forEach((key) => {
      const [idparticipant, type, tone, slotNo] = key.split(":").map(Number);
      const color = normalize(cells[key]);
      if (color) items.push({ idparticipant, type, tone, slotNo, color });
      else if (records[key] && Number(records[key].state) === 1) removed.push({ ...records[key], idparticipant, type, tone, slotNo });
    });
    if (removed.length && !window.confirm(`Se eliminarán ${removed.length} uniforme${removed.length === 1 ? "" : "s"}. ¿Continuar?`)) return;

    setSaving(true);
    try {
      const result = await saveUniformMatrix({ idevent, idsport, items, removed });
      toast.success(`Uniformes guardados: ${result.created || 0} nuevos, ${result.updated || 0} actualizados, ${result.deleted || 0} eliminados.`);
      await load();
    } catch (error) {
      console.error(error);
      toast.error("No se pudieron guardar los uniformes");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="rounded-2xl bg-white p-8 text-center font-semibold text-gray-500 shadow">Cargando uniformes...</div>;

  return (
    <section className="rounded-2xl bg-white p-4 shadow-md">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl font-black text-gray-900">Uniformes por institución</h2>
          <p className="text-sm text-gray-500">
            {readOnly
              ? "Solo lectura: las opciones de uniforme se gestionan en Administración General → Uniformes."
              : "Edita varias celdas y guarda todos los cambios en una sola operación."}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={load} className="inline-flex items-center gap-2 rounded-xl border border-gray-200 px-3 py-2 text-sm font-bold text-gray-600 hover:bg-gray-50"><FaSync /> Recargar</button>
          {!readOnly && <UniformMatrixColumnPicker catalog={catalog} onAdd={addColumn} />}
          {!readOnly && <button type="button" onClick={save} disabled={saving || !dirtyKeys.size} className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2 text-sm font-bold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-gray-300"><FaSave /> {saving ? "Guardando..." : `Guardar cambios${dirtyKeys.size ? ` (${dirtyKeys.size})` : ""}`}</button>}
        </div>
      </div>

      <div className="mt-4 overflow-auto rounded-xl border border-gray-200">
        <table className="min-w-max border-collapse text-sm">
          <thead className="sticky top-0 z-20 bg-gray-100">
            <tr>
              <th className="sticky left-0 z-30 min-w-64 border-b border-r border-gray-200 bg-gray-100 px-4 py-3 text-left font-black text-gray-700">Institución</th>
              {columns.map((column) => (
                <th key={uniformColumnKey(column)} className="min-w-40 border-b border-gray-200 px-3 py-3 text-left font-black text-gray-700">{column.label} {column.slotNo}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {participants.map((participant) => (
              <tr
                key={participant.idparticipant}
                className="
        group
        transition-colors duration-150
        hover:bg-slate-50/80
      "
              >
                <td
                  className="
          sticky left-0 z-10
          border-r border-gray-200
          bg-white px-4 py-3
          transition-colors duration-150
          group-hover:bg-slate-50
        "
                >
                  <div className="flex items-center gap-3">
                    {participant.institution?.logo ? (
                      <img
                        src={participant.institution.logo}
                        alt=""
                        className="h-9 w-9 rounded-lg object-contain"
                      />
                    ) : (
                      <span className="h-9 w-9 rounded-lg bg-gray-100" />
                    )}

                    <div>
                      <p className="font-black text-gray-900">
                        {participant.institution?.abbreviation || "-"}
                      </p>

                      <p className="max-w-48 truncate text-xs text-gray-500">
                        {participant.institution?.name || "Institución sin nombre"}
                      </p>
                    </div>
                  </div>
                </td>

                {columns.map((column) => {
                  const key = uniformCellKey({
                    idparticipant: participant.idparticipant,
                    ...column,
                  });

                  const isDirty = dirtyKeys.has(key);

                  return (
                    <td
                      key={uniformColumnKey(column)}
                      className={`
              relative px-3 py-2
              transition-all duration-150
              focus-within:z-10
              focus-within:bg-indigo-50/70
              focus-within:ring-2
              focus-within:ring-inset
              focus-within:ring-indigo-200
              ${isDirty ? "bg-blue-50/60" : "bg-white"}
            `}
                    >
                      <UniformMatrixCell
                        value={cells[key] || ""}
                        dirty={isDirty}
                        readOnly={readOnly}
                        onChange={(value) =>
                          setCells((current) => ({
                            ...current,
                            [key]: value,
                          }))
                        }
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
